import { inject, Injectable, OnDestroy } from '@angular/core';
import { concatMap, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import {
  CucmDirectoryNumbersReport,
  DirectoryNumberReportTeamsNumberEntry,
  DirectoryNumberReportTeamsUpnEntry,
  IgnoredDirectoryNumber,
  JobState,
  JobStatus,
} from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { delayWhen, filter, map, take, tap } from 'rxjs/operators';
import { MigrationsCucmDirectoryNumbersPollingContext } from './migrations-cucm-directory-numbers-polling.context';
import { FileDownloadService } from '../../../shared/services/file-download.service';

@Injectable()
export class MigrationsCucmDirectoryNumberContext implements OnDestroy {
  private _http = inject(HttpClient);
  private _route = inject(ActivatedRoute);
  private _migrationsCucmDirectoryNumbersPollingContext = inject(MigrationsCucmDirectoryNumbersPollingContext);
  private _fileDownloadService = inject(FileDownloadService);

  private _stateSource = new ReplaySubject<CucmDirectoryNumbersReport>(1);
  state$ = this._stateSource.asObservable();

  private _jobStateSource = new ReplaySubject<JobStatus>(1);
  jobState$ = this._jobStateSource.asObservable();

  private _baseUrl = `/services/microsoft/migrations/tenants/${this._route.snapshot.params.tenantId}/exports/cisco/${this._route.snapshot.params.id}/directory-numbers-report`;

  constructor() {
    this._init().subscribe();
  }

  ngOnDestroy(): void {
    this._migrationsCucmDirectoryNumbersPollingContext.stopPolling();
  }

  refresh(): Observable<void> {
    return this._refresh().pipe(switchMap(() => this._init()));
  }

  exportDirectoryNumbersReport(): Observable<void> {
    return this._fileDownloadService.downloadFile(`${this._baseUrl}/export`);
  }

  updateDirectoryNumberUpn(routePartitionId: string, dnToUpdate: string, updatedUpn: string): Observable<void> {
    const body: DirectoryNumberReportTeamsUpnEntry = {
      teamsUserPrincipalName: updatedUpn,
    };

    return this._http
      .put<void>(
        `${this._baseUrl}/route-partition/${routePartitionId}/directory-number/${dnToUpdate}/teamsUserPrincipalName`,
        body,
      )
      .pipe(delayWhen(() => this.softRefresh()));
  }

  updateDirectoryNumberTeamsNumber(
    routePartitionId: string,
    dnToUpdate: string,
    teamsNumber: string,
  ): Observable<void> {
    const body: DirectoryNumberReportTeamsNumberEntry = {
      teamsNumber,
    };
    return this._http
      .put<void>(
        `${this._baseUrl}/route-partition/${routePartitionId}/directory-number/${dnToUpdate}/teamsNumber`,
        body,
      )
      .pipe(delayWhen(() => this.softRefresh()));
  }

  updateIgnoredDirectoryNumbers(ignoredDirectoryNumbers: IgnoredDirectoryNumber[]): Observable<void> {
    return this._http
      .put<void>(
        `/services/microsoft/migrations/tenants/${this._route.snapshot.params.tenantId}/exports/cisco/${this._route.snapshot.params.id}/ignored-directory-numbers`,
        ignoredDirectoryNumbers,
      )
      .pipe(delayWhen(() => this.softRefresh()));
  }

  private _init(): Observable<void> {
    this._migrationsCucmDirectoryNumbersPollingContext.startPolling();

    return this._migrationsCucmDirectoryNumbersPollingContext.state$.pipe(
      tap((status) => this._jobStateSource.next(status)),
      filter((status) => status.jobState === JobState.IDLE),
      concatMap((status: JobStatus) => {
        if (!status.lastRunTime) {
          return this._refresh();
        }
        this._migrationsCucmDirectoryNumbersPollingContext.stopPolling();
        if (!status.error) {
          return this._get();
        }
        return of({
          cucmDirectoryNumbers: [],
          endUsers: [],
          mostRecentCdrTimestamp: null,
          oldestCdrTimestamp: null,
          totalNumberOfCdrs: 0,
        });
      }),
      filter((report) => !!report),
      map((report: CucmDirectoryNumbersReport) => {
        this._stateSource.next(report);
      }),
      take(1),
    );
  }

  private _get(): Observable<CucmDirectoryNumbersReport> {
    return this._http.get<CucmDirectoryNumbersReport>(this._baseUrl);
  }

  softRefresh(): Observable<void> {
    return this._get().pipe(
      map((report) => {
        this._stateSource.next(report);
      }),
    );
  }

  private _refresh(): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/refresh`, null);
  }
}
